import { useEffect } from 'react';
import { format } from 'date-fns';

// material-ui
import { ButtonBase, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

// project imports
import { useDispatch, useSelector } from 'store';
import { getRecentConversations, openConversation } from 'store/slices/askJivan';
import { answerStyle, threadField } from './messageList';

// each conversation is a card on the field, the shape an answer takes in the thread
const conversationCard = (theme) => ({
    ...answerStyle(theme),
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    px: 1.5,
    py: 1,
    borderRadius: 2,
    border: '1px solid transparent',
    '&:hover, &:focus-visible': {
        borderColor: theme.palette.primary.main,
        bgcolor: alpha(theme.palette.primary.light, 0.4)
    },
    '&.Mui-disabled': { opacity: 0.6 }
});

// The user's own recent conversations; picking one reopens it in the chat view.
const ConversationHistory = () => {
    const dispatch = useDispatch();
    const { recentConversations, pending, loadingConversation } = useSelector((state) => state.askJivan);

    useEffect(() => {
        dispatch(getRecentConversations());
    }, [dispatch]);

    if (recentConversations.length === 0) {
        return (
            <Stack sx={{ flex: 1, p: 2, bgcolor: threadField }}>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={(theme) => ({ ...answerStyle(theme), px: 1.5, py: 1, borderRadius: 2 })}
                >
                    No conversations yet.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack gap={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: threadField }} data-testid="ask-jivan-history">
            {recentConversations.map((conversation) => (
                <ButtonBase
                    key={conversation.id}
                    // swapping threads mid-question or mid-load would strand the answer
                    disabled={pending || loadingConversation}
                    onClick={() => dispatch(openConversation(conversation.id))}
                    sx={conversationCard}
                >
                    <Stack sx={{ width: '100%', minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            {conversation.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {format(new Date(conversation.lastMessageAt), 'MMM d, h:mm a')}
                        </Typography>
                    </Stack>
                </ButtonBase>
            ))}
        </Stack>
    );
};

export default ConversationHistory;
